<script setup>
import { computed, toRefs } from 'vue'
import { Country } from 'country-state-city'
import StateField from './StateField.vue'
import BaseListbox from '@/components/Listbox/BaseListbox.vue'
import { useUserStore } from '@/stores/UserStore'

const userStore = useUserStore()

const props = defineProps({
  editMode: { type: Boolean, default: true },
  location: { type: Object, default: {} },
  errors: { type: Array, default: [] },
  hideFields: { type: Array, default: [] },
  hideLabel: { type: Boolean, default: false },
  supportedCountries: { type: Array, default: [] },
  stateNextToCountry: { type: Boolean, default: false }
})

const { editMode, location, errors } = toRefs(props)

const countryOptions = computed(() => {
  const list = props.supportedCountries.length > 0 ? props.supportedCountries : Country.getAllCountries()

  return list.map(country => ({
    value: country.isoCode,
    label: `${country.name}`
  }))
})

</script>
<template>
  <section>
    <div class="flex flex-col gap-y-4 text-xs font-normal text-gray-900">
      <div :class="{ 'flex gap-x-4': stateNextToCountry }">
        <div class="block" :class="{ 'flex-1': stateNextToCountry }">
          <template v-if="editMode">
            <BaseListbox
              v-model="location.country"
              :options="countryOptions"
              :label="hideLabel ? '' : 'Country'"
              placeholder="Select Country"
              :error="errors.includes('country')"
              button-class="w-full"
              :is-capitalize="false"
              :label-class="hideLabel ? '' : 'controller-label'"
            />
            <small v-if="errors.includes('country')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <div v-else class="flex flex-col items-start ">
            <span class="controller-label">Country</span>
            <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.country }">{{ countryOptions.find(option => option.value === location.country)?.label || '-' }}</p>
          </div>
        </div>

        <StateField
          v-if="stateNextToCountry && !hideFields.includes('state')"
          class="flex-1"
          :location="location"
          :errors="errors"
          :edit-mode="editMode"
          :hide-label="hideLabel"
        />
      </div>

      <label class="block" v-if="!hideFields.includes('address_1')">
        <span class="controller-label">Address Line 1</span>
        <template v-if="editMode">
          <input
            type="text"
            class="input-controller font-normal py-2"
            placeholder="Address Line 1"
            :class="{'error': errors.includes('address_1')}"
            v-model="location.address_1"
          />
          <small v-if="errors.includes('address_1')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
       
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_1 }" v-else>{{ location.address_1 || '-' }}</p>
      </label>
     
      <label class="block" v-if="!hideFields.includes('address_2')">
        <span class="controller-label">Address Line 2 <span class="text-gray-500">(Optional)</span></span>
        <template v-if="editMode">
          <input
            type="text"
            class="input-controller font-normal py-2"
            placeholder="Address Line 2 (Optional)"
            v-model="location.address_2"
          />
        </template>
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_2 }" v-else>{{ location.address_2 || '-' }}</p>
      </label>
     
      <div class="flex gap-x-4">
        <label class="block" v-if="!hideFields.includes('city')">
          <span class="controller-label">City</span>
          <template v-if="editMode">
            <input
              type="text"
              class="input-controller font-normal py-2"
              placeholder="City"
              :class="{'error': errors.includes('city')}"
              v-model="location.city"
            />
            <small v-if="errors.includes('city')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.city }" v-else>{{ location.city || '-' }}</p>
        </label>

        <StateField
          v-if="!stateNextToCountry && !hideFields.includes('state')"
          :location="location"
          :errors="errors"
          :edit-mode="editMode"
          :hide-label="hideLabel"
        />

        <label class="block" v-if="!hideFields.includes('postal_code')">
          <span class="controller-label">{{ location.country === 'US' ? 'ZIP Code' : 'Postal Code' }}</span>
          <template v-if="editMode">
            <input
              type="text"
              class="input-controller font-normal py-2"
              :placeholder="location.country === 'US' ? 'ZIP Code' : 'Postal Code'"
              :class="{'error': errors.includes('postal_code')}"
              v-model="location.postal_code"
            />
            <small v-if="errors.includes('postal_code')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.postal_code }" v-else>{{ location.postal_code || '-' }}</p>
        </label>
      </div> 
    </div>
  </section>
</template>